import React, { useCallback, useEffect, useRef, useState } from "react";
import { CircularProgress, Grid, InputAdornment, Stack } from "@mui/material";
import { UseFormReturn, Path } from "react-hook-form";
import { SectionTitle } from "../../../components/Checkout/SectionTitle";
import { ControlledTextField } from "../../../components/forms/ControlledTextField";
import { ControlledCountryPhoneField } from "../../../components/forms/ControlledCountryPhoneField";
import { ControlledSelect } from "../../../components/forms/ControlledSelect";
import { textFieldStyle } from "../../../components/Checkout/CouponStyles";
import { CHECKOUT_MAXLEN } from "../../../utils/checkoutFieldMaxLengths";
import { HttpAfip } from "../../../http/httpAfip";
import { DOC_DNI_ID, OrderDeliveryAddressFields } from "../../../types/purchaseOrder/Order";
import { ActualizacionDatosCatalogos } from "../useActualizacionDatosForm";
import { ActualizacionDatosFormValues } from "../ActualizacionDatosTypes";

const CUIT_LENGTH = 11;

interface DatosPersonalesSeccionProps {
    methods: UseFormReturn<ActualizacionDatosFormValues>;
    catalogos: ActualizacionDatosCatalogos;
}

export const DatosPersonalesSeccion: React.FC<DatosPersonalesSeccionProps> = ({
    methods,
    catalogos,
}) => {
    const baseName = "datosPersonales";
    const docTipoName = `${baseName}.tinCodDocumentoTipo` as Path<ActualizacionDatosFormValues>;
    const countryCodeName = `${baseName}.codigoArea` as Path<ActualizacionDatosFormValues>;
    const phoneName = `${baseName}.telefono` as Path<ActualizacionDatosFormValues>;
    const razonSocialName = `${baseName}.vchRazonSocial` as Path<ActualizacionDatosFormValues>;
    const afipTipoName =
        `${baseName}.tinCodAfipResponsableTipo` as Path<ActualizacionDatosFormValues>;

    const silentTrigger = useCallback(
        () => Promise.resolve(true) as ReturnType<typeof methods.trigger>,
        [],
    );

    const docTipo = methods.watch(docTipoName) as number | null | undefined;
    const esDNI = docTipo === DOC_DNI_ID;

    const afipResponsableTiposFiltrados =
        docTipo == null
            ? catalogos.afipResponsableTipos
            : catalogos.afipResponsableTipos.filter(
                  (t) => t.codDocumentoTipo == null || t.codDocumentoTipo === docTipo,
              );

    const [isLoadingAfip, setIsLoadingAfip] = useState(false);
    const lastFetchedCuitRef = useRef<string | null>(null);

    const setAfipField = useCallback((name: any, value: any) => {
        methods.setValue(name, value, {
            shouldValidate: false,
            shouldDirty: false,
            shouldTouch: false,
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const clearAfipFields = useCallback(() => {
        setAfipField(razonSocialName, null);
        setAfipField(afipTipoName, null);
        setAfipField(`domicilioFacturacion.${OrderDeliveryAddressFields.Street}`, null);
        setAfipField(`domicilioFacturacion.${OrderDeliveryAddressFields.StreetNumber}`, null);
        setAfipField(`domicilioFacturacion.${OrderDeliveryAddressFields.LocalityDesc}`, null);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Sync con ARCA/AFIP: se dispara mirando el valor del CUIT con methods.watch (no por el
    // onChange del input) para que funcione siempre, incluso si el valor lo setea el form.
    // Igual que en el checkout: al completar los 11 digitos consulta la constancia y
    // autocompleta razon social, condicion frente al IVA y domicilio de facturacion.
    const cuitWatch = methods.watch(`${baseName}.vchCuit` as Path<ActualizacionDatosFormValues>) as
        | string
        | null
        | undefined;

    useEffect(() => {
        const digits = (cuitWatch ?? "").replace(/\D/g, "");
        if (digits.length !== CUIT_LENGTH || lastFetchedCuitRef.current === digits) return;

        const timeout = window.setTimeout(() => {
            lastFetchedCuitRef.current = digits;
            setIsLoadingAfip(true);
            HttpAfip.obtenerConstanciaInscripcion(digits)
                .then((constancia) => {
                    if (!constancia) {
                        clearAfipFields();
                        return;
                    }

                    const razonSocial =
                        constancia.razonSocial?.trim() ||
                        [constancia.apellido, constancia.nombre].filter(Boolean).join(", ") ||
                        null;

                    setAfipField(razonSocialName, razonSocial);
                    setAfipField(afipTipoName, constancia.codResponsableAfipTipo ?? null);

                    const dom = constancia.domicilioFiscal;
                    setAfipField(
                        `domicilioFacturacion.${OrderDeliveryAddressFields.Street}`,
                        dom.calle || dom.direccion || null,
                    );
                    setAfipField(
                        `domicilioFacturacion.${OrderDeliveryAddressFields.StreetNumber}`,
                        dom.nro || null,
                    );
                    setAfipField(
                        `domicilioFacturacion.${OrderDeliveryAddressFields.LocalityDesc}`,
                        dom.localidad || null,
                    );
                })
                .catch(clearAfipFields)
                .finally(() => setIsLoadingAfip(false));
        }, 500);

        return () => window.clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cuitWatch]);

    const firstNamePath = `${baseName}.nombre` as const;
    const lastNamePath = `${baseName}.apellido` as const;

    const handleNombreChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const apellido = (methods.getValues(lastNamePath as any) as string) ?? "";
        const generated = [apellido, e.target.value].filter(Boolean).join(", ");
        methods.setValue(razonSocialName, (generated || null) as any, { shouldValidate: false });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleApellidoChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const nombre = (methods.getValues(firstNamePath as any) as string) ?? "";
        const generated = [e.target.value, nombre].filter(Boolean).join(", ");
        methods.setValue(razonSocialName, (generated || null) as any, { shouldValidate: false });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Stack mb={2}>
            <SectionTitle>Datos Personales</SectionTitle>

            <Grid container spacing={2}>
                {esDNI ? (
                    <>
                        <Grid size={{ xs: 12, sm: 2 }}>
                            <ControlledSelect
                                control={methods.control}
                                name={docTipoName}
                                label="Tipo"
                                options={catalogos.documentoTipos}
                                sx={textFieldStyle}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 2 }}>
                            <ControlledTextField
                                control={methods.control}
                                label="Nro Documento"
                                name={`${baseName}.documento`}
                                required
                                inputProps={{
                                    maxLength: CHECKOUT_MAXLEN.vchDni,
                                    inputMode: "numeric",
                                    pattern: "[0-9]*",
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <ControlledTextField
                                control={methods.control}
                                label="Nombre"
                                name={`${baseName}.nombre`}
                                required
                                onChange={handleNombreChange}
                                inputProps={{ maxLength: CHECKOUT_MAXLEN.vchNombre }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <ControlledTextField
                                control={methods.control}
                                label="Apellido"
                                name={`${baseName}.apellido`}
                                required
                                onChange={handleApellidoChange}
                                inputProps={{ maxLength: CHECKOUT_MAXLEN.vchApellido }}
                            />
                        </Grid>
                    </>
                ) : (
                    <>
                        <Grid size={{ xs: 12, sm: 2 }}>
                            <ControlledSelect
                                control={methods.control}
                                name={docTipoName}
                                label="Tipo"
                                options={catalogos.documentoTipos}
                                sx={textFieldStyle}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 2 }}>
                            <ControlledTextField
                                control={methods.control}
                                label="CUIT / CUIL"
                                name={`${baseName}.vchCuit`}
                                required={docTipo != null}
                                inputProps={{
                                    maxLength: CHECKOUT_MAXLEN.vchCuit,
                                    inputMode: "numeric",
                                    pattern: "[0-9]*",
                                }}
                                InputProps={{
                                    endAdornment: isLoadingAfip ? (
                                        <InputAdornment position="end">
                                            <CircularProgress size={16} />
                                        </InputAdornment>
                                    ) : undefined,
                                }}
                            />
                        </Grid>
                        {docTipo != null && (
                            <>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <ControlledTextField
                                        control={methods.control}
                                        label="Razón social"
                                        name={razonSocialName}
                                        required
                                        inputProps={{ maxLength: CHECKOUT_MAXLEN.vchRazonSocial }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <ControlledSelect
                                        control={methods.control}
                                        name={afipTipoName}
                                        label="Condición frente al IVA"
                                        options={afipResponsableTiposFiltrados}
                                        sx={textFieldStyle}
                                    />
                                </Grid>
                            </>
                        )}
                    </>
                )}

                <Grid size={12}>
                    <ControlledCountryPhoneField<ActualizacionDatosFormValues>
                        control={methods.control}
                        getValues={methods.getValues}
                        setValue={methods.setValue}
                        trigger={silentTrigger}
                        countryCodeName={countryCodeName}
                        phoneName={phoneName}
                        required
                        phoneMaxLength={CHECKOUT_MAXLEN.vchTelefono}
                        defaultCountryIso2="AR"
                        phoneHelperText='Ingresá código de área y número. Ej AR celular: " 9 11 1234 5678".'
                    />
                </Grid>
            </Grid>
        </Stack>
    );
};
